import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { BackButton } from "@/components/BackButton";
import { Reveal } from "@/components/Reveal";

const S = ({ num, title, children }) => (
  <section className="mt-10" data-testid={`terms-section-${num}`}>
    <h2 className="font-display text-lg md:text-xl font-bold text-navy">
      {num}. {title}
    </h2>
    <div className="mt-3 space-y-3 text-sm md:text-base text-slate-600 leading-relaxed">{children}</div>
  </section>
);

const Ul = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1">
    {items.map((i) => (
      <li key={i}>{i}</li>
    ))}
  </ul>
);

export default function TermsPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-12" data-testid="terms-page">
      <Seo
        title="Terms & Conditions"
        description="The terms that govern shopping on the BTMEDU website and app — accounts, orders, payment, delivery, collection, returns and refunds."
      />
      <BackButton to="/" label="Back to home" testId="terms-back-button" />
      <Reveal>
        <header className="mt-6 border-b border-slate-200 pb-8">
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-navy tracking-tight">
            Terms &amp; Conditions
          </h1>
          <p className="mt-3 text-sm md:text-base text-slate-500">
            BTMEDU (PTY) LTD · Company Registration Number: 2018/531458/07 · Last updated: October 2026
          </p>
          <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed">
            These terms govern your use of the BTMEDU website and app and every purchase you make from us. By
            browsing the site, creating an account or placing an order, you agree to these terms. Please read them
            together with our <Link to="/privacy" className="text-brand font-semibold hover:underline" data-testid="terms-privacy-link">Privacy Policy (POPIA)</Link>.
          </p>
        </header>

        <S num="1" title="About us">
          <p>
            BTMEDU (PTY) LTD (“BTMEDU”, “we”, “us”) is a South African retailer of stationery, textbooks, religious
            books, art &amp; craft supplies, educational toys and corporate gifts, trading from Shop 16 North Park
            Mall, 256 Burger Street, Pretoria North, and online at www.btmedu.co.za.
          </p>
        </S>

        <S num="2" title="Customer accounts">
          <Ul
            items={[
              "You may shop as a guest or create an account. When you create an account you must provide accurate information and accept these Terms and our Privacy Policy.",
              "You are responsible for keeping your password confidential and for all activity on your account.",
              "We may suspend or close an account that is used fraudulently, abusively or in breach of these terms.",
            ]}
          />
        </S>

        <S num="3" title="Orders, pricing and availability">
          <Ul
            items={[
              "All prices are shown in South African Rand (ZAR) and include VAT where applicable.",
              "An order is an offer to buy. A contract is formed only when we confirm the order and receive payment (or payment authorisation) for it.",
              "Stock levels are shown in good faith but can change. If an item becomes unavailable after you order, we will notify you and offer a replacement, backorder or a refund for that item.",
              "If a pricing or product information error occurs, we may cancel the affected order and refund you in full — even after confirmation.",
              "School list items are supplied per the school's prescribed list for the selected grade; required and optional items are marked at the time of selection.",
            ]}
          />
        </S>

        <S num="4" title="Payment">
          <Ul
            items={[
              "We accept online payment via PayFast (card, Instant EFT and other methods PayFast supports).",
              "We do not store your full card details on our systems — payments are processed by PayFast.",
              "Your order confirmation email serves as your electronic tax invoice. Please keep it for your records.",
              "Orders that remain unpaid may be cancelled and the stock released.",
            ]}
          />
        </S>

        <S num="5" title="Delivery and collection">
          <Ul
            items={[
              "Delivery is a flat R85 per order, and FREE for orders of R750 or more.",
              "You may also choose free collection at Shop 16 North Park Mall, 256 Burger Street, Pretoria North. We will notify you when your order is ready for collection.",
              "Delivery times are estimates and may be affected by courier schedules, weather and peak seasons (such as back-to-school).",
              "Please ensure your delivery address and contact number are correct — we cannot be responsible for delays caused by incorrect details.",
              "Risk in the products passes to you on delivery or collection.",
            ]}
          />
        </S>

        <S num="6" title="Returns and refunds">
          <Ul
            items={[
              "If a product arrives damaged, defective or incorrect, contact us within 7 days of delivery or collection and we will replace it or refund you, in line with the Consumer Protection Act 68 of 2008.",
              "Unwanted items in their original, unused and unopened condition may be returned within 14 days for an exchange or refund. Digital or sealed items (for example software or CDs) that have been opened cannot be returned unless defective.",
              "Refunds are made to the original payment method once the returned goods have been received and inspected.",
              "To start a return, email orders@btmedu.co.za or call/WhatsApp 061 081 4598 with your order number.",
            ]}
          />
        </S>

        <S num="7" title="Stationery Stokvel">
          <p>
            Where the Stationery Stokvel is offered, its specific rules (enrolment, contributions, benefits and
            redemption) will be published and provided to participating members. Those rules apply in addition to
            these terms.
          </p>
        </S>

        <S num="8" title="Acceptable use">
          <Ul
            items={[
              "You may not misuse the website or app, attempt to gain unauthorised access, interfere with other users, or use the site for unlawful purposes.",
              "Product images and descriptions are provided to help you choose; minor differences (for example packaging changes by a supplier) do not constitute a defect.",
            ]}
          />
        </S>

        <S num="9" title="Intellectual property">
          <p>
            The BTMEDU name and logo, website design, text and graphics are the property of BTMEDU (PTY) LTD or its
            licensors. Product names and brands belong to their respective owners. You may not copy or reuse our
            content without written permission.
          </p>
        </S>

        <S num="10" title="Limitation of liability">
          <p>
            Nothing in these terms limits your rights under the Consumer Protection Act or other applicable law. To the
            extent permitted by law, BTMEDU is not liable for indirect or consequential losses (such as loss of income)
            arising from use of the website or purchase of products. Our total liability for any claim relating to an
            order is limited to the amount you paid for that order.
          </p>
        </S>

        <S num="11" title="Governing law">
          <p>
            These terms are governed by the laws of the Republic of South Africa, including the Consumer Protection Act
            68 of 2008 and the Electronic Communications and Transactions Act 25 of 2002. Any dispute will be subject
            to the jurisdiction of the South African courts.
          </p>
        </S>

        <S num="12" title="Changes to these terms">
          <p>
            We may update these terms from time to time. The version published on this page applies to orders placed
            while it is in force. Material changes will be communicated where the law requires.
          </p>
        </S>

        <S num="13" title="Contact us">
          <Ul
            items={[
              "Email: orders@btmedu.co.za",
              "Call / WhatsApp: 061 081 4598",
              "Store: Shop 16 North Park Mall, 256 Burger Street, Pretoria North",
            ]}
          />
        </S>
      </Reveal>
    </div>
  );
}
